<template>
    <div class="schedule_calendar_wrap" style="outline: none" ref="scroll" :tabindex="0">
        <div v-if="isLoading" class="loading_bg fixed" style="z-index: 999">
            <div class="loading_wrap">
                <div class="loading_obj">
                    <svg class="spinner" width="60px" height="60px" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                        <circle class="path" fill="none" stroke-width="6" stroke-linecap="round" cx="33" cy="33" r="30"></circle>
                    </svg>
                </div>
            </div>
        </div>
        <div class="weeks">{{ $t('common.sun') }}</div>
        <div class="weeks">{{ $t('common.mon') }}</div>
        <div class="weeks">{{ $t('common.tue') }}</div>
        <div class="weeks">{{ $t('common.wed') }}</div>
        <div class="weeks">{{ $t('common.thu') }}</div>
        <div class="weeks">{{ $t('common.fri') }}</div>
        <div class="weeks">{{ $t('common.sat') }}</div>
        <!-- 아래부터 캘린더 -->
        <CalendarItem
            v-for="(date, index) in dateArray"
            :key="index"
            :ref="'edit_layer_' + index"
            :selectedMonth="selectedDate"
            :date="date"
            :workRecords="refinedRecords"
            :holidays="holidays"
            :keyEventController="keyEventController"
        />

        <SearchLayer :scroll="$refs.scroll" :keyEventController="keyEventController" />

    </div>
</template>

<script>
import { deepCopy } from '@/util/deep-copy'
import SearchLayer from './CalendarEditItem/SearchLayer/CalendarSearchLayer.vue'
import CalendarItem from './CalendarItem.vue'

import { sheetState } from '../../../../../../sheet-state'
import { workerState, setNeedUpdatePastedRecord, showSearchLayer } from '../../../worker-state'
import { mapState } from 'vuex'
const moment = require('@/util/MPMoment')

import KeyEventController from '@/controller/EventController'
import { useQueryClient } from '@tanstack/vue-query'
import { isApiErrorWithCode } from '@/apis/instances/check-api-response'
import { v2WorkRecordFindDateQueryOptions } from '@/composables/queries/v2workrecord/use-v2-workrecord-find-date-query'
import { v2WorkHolidayFindDateQueryOptions } from '@/composables/queries/v2workholiday/use-v2-workholiday-find-date-query'
import { useManagingWorkers } from '@/composables/use-managing-workers'
import { useV2WorkRecordUpdateMutation } from '@/composables/queries/v2workrecord/use-v2-workrecord-update-mutation'
import { useV2WorkRecordUpdateBatchMutation } from '@/composables/queries/v2workrecord/use-v2-workrecord-update-batch-mutation'
import { useV2WorkRecordRemoveMutation } from '@/composables/queries/v2workrecord/use-v2-workrecord-remove-mutation'
import { useV2WorkRecordSendPushMutation } from '@/composables/queries/v2workrecord/use-v2-workrecord-send-push-mutation'

export default {
    setup() {
        const queryClient = useQueryClient()
        const { employees: allEmployees } = useManagingWorkers()
        const { mutateAsync: updateWorkRecord } = useV2WorkRecordUpdateMutation()
        const { mutateAsync: updateBatchWorkRecord } = useV2WorkRecordUpdateBatchMutation()
        const { mutateAsync: removeWorkRecord } = useV2WorkRecordRemoveMutation()
        const { mutateAsync: sendPushWorkRecord } = useV2WorkRecordSendPushMutation()
        return { queryClient, allEmployees, updateWorkRecord, updateBatchWorkRecord, removeWorkRecord, sendPushWorkRecord }
    },
    components: {
        SearchLayer,
        CalendarItem
    },
    data() {
        return {
            isLoading: false,
            worker: workerState,
            workRecords: [],
            refinedRecords: [],
            holidays: [],

            keyEventController: undefined,

            recordUpdateQueue: []
        }
    },
    watch: {
        'remote_notification.d_v2WorkRecordUpdateBatch_el': {
            handler: function (newValue) {
                if (this.checkNeedReload(newValue) == true) {
                    if (this.isEditMode == true) {
                        return
                    }
                    if (this.isShowEditLayer == true) {
                        return
                    }
                    this.loadData()
                }
            }
        },
        'remote_notification.d_v2WorkHolidayCreate_el': {
            handler: function () {
                if (this.isEditMode || this.isShowEditLayer == true) {
                    return
                }
                this.loadHoliday()
            }
        },
        selectedDate: {
            immediate: true,
            deep: true,
            handler: function (newValue, oldValue) {
                if (newValue == undefined) {
                    return
                }
                if (newValue == oldValue) {
                    return
                }
                this.loadData()
            }
        },
        dateArray: {
            handler: function (newValue) {
                this.worker.dateArray = newValue
                this.loadData()
            }
        },
        isEditMode: {
            handler: function (newValue) {
                if (newValue == true) {
                    this.worker.updateBatch = this.updateBatch
                    this.worker.focusedRecord = undefined
                    this.worker.focusedElement = undefined
                    this.registKEC()
                } else if (newValue == false) {
                    this.worker.updateBatch = undefined
                    this.unregistKEC()
                }
            }
        },
        'worker.focusedElement': {
            handler: function (newValue) {
                if (newValue == undefined) {
                    return
                }
                this.moveToElement(newValue)
            }
        },
        'worker.needUpdateRecords': {
            handler: function (newValue) {
                if (newValue == true) {
                    this.$nextTick(async () => {
                        this.worker.needUpdateRecords = false
                        this.isLoading = true
                        await this.loadWorkRecords()
                        this.isLoading = false
                    })
                }
            }
        },
        'worker.needUpdateHolydays': {
            handler: function (newValue) {
                if (newValue == true) {
                    this.$nextTick(async () => {
                        this.worker.needUpdateHolydays = false
                        await this.loadData()
                    })
                }
            }
        },
        'worker.needUpdatePastedRecord': {
            immediate: true,
            handler: function (newValue) {
                if (newValue == false) {
                    return
                }
                this.refineRecords()
                setNeedUpdatePastedRecord(false)
            }
        },
        workCodeds: {
            deep: true,
            handler: function () {
                this.refineRecords()
            }
        }
    },
    computed: {
        ...mapState(['remote_notification', 'SaaS']),
        isEditMode: function () {
            return this.worker.isModifyMode
        },
        isShowEditLayer: function () {
            return this.worker.calendarEditLayer_Date != undefined
        },
        selectedDate: function () {
            return moment(this.worker.selectedDate).startOf('month')
        },
        workCodeds: function () {
            return this.worker.workCodes ?? []
        },
        selectedWorkCodes: function () {
            return this.workCodes?.filter((c) => c.isSelected == true) ?? []
        },
        dateArray: function () {
            let start = moment(this.selectedDate).tz(this.timezone).startOf('weeks')
            let array = []
            for (let i = 0; i < 7 * 6; i++) {
                let current = moment(start).add(i, 'days').startOf('days').startOf('hours')
                array.push(current)
            }
            return array
        },
        startDateString: function () {
            return this.dateArray[0]?.format() ?? moment().format()
        },
        endDateString: function () {
            return this.dateArray[this.dateArray.length - 1]?.format() ?? moment().format()
        },
        employees: function () {
            return this.allEmployees.filter((e) => e.isSelected == true) ?? []
        },
        isShowCustomTimeCode: function () {
            return this.worker.isShowCustomTimeCode
        },
        reservationSetting: function () {
            // 구 코드는 wDataController(근무일정 상태)에서 읽어 항상 undefined 였다 —
            // 형제 컴포넌트(CalendarItem)와 동일하게 메인 컨트롤러에서 읽는다
            return sheetState.reservationSetting
        },
        possibleTimes: function () {
            return this.reservationSetting.possibleTimes
        },
        timezone: function () {
            const tz = this.SaaS?.timezone
            if (tz == undefined || tz == '') {
                return 'Asia/Seoul'
            }
            return this.SaaS?.timezone
        }
    },
    mounted() {
        this.keyEventController = new KeyEventController(['keydown'])
        this.worker.keyEventController = this.keyEventController
    },
    beforeDestroy() {
        this.keyEventController.destroyObject()
        this.keyEventController = undefined
        this.worker.keyEventController = undefined
        this.worker.copiedRecords = undefined
    },
    methods: {
        // MARK: - Private
        refineRecords() {
            let array = deepCopy(this.workRecords) ?? []

            // work code title refine
            for (let i = 0; i < array.length; i++) {
                let r = array[i]
                if (r.isCustomized != true) {
                    let code = this.worker.workCodes.find((c) => c._id == r.workCodeID?._id)
                    if (code != undefined) {
                        r.workCodeID = code
                    }
                }
            }

            // old data refine
            if (this.worker.oldWorkCodeRecords != undefined) {
                let keys = Object.keys(this.worker.oldWorkCodeRecords)
                for (let ki = 0; ki < keys.length; ki++) {
                    let dateKey = keys[ki]
                    if (this.worker.oldWorkCodeRecords[dateKey].isModified != true) {
                        continue
                    }
                    let oldRecords = this.worker.oldWorkCodeRecords[dateKey]?.modifiedRecords
                    for (let i = 0; i < oldRecords.length; i++) {
                        let or = oldRecords[i]
                        if (or.isCustomized == true) {
                            continue
                        } // customized 는 아래에서.
                        let filter = function (r) {
                            let hasSameID = r._id == or._id
                            let hasSameDate = moment(r.date).format() == moment(or.date).format()
                            let hasSameWorkCodeID = r.workCodeID?._id == or.workCodeID?._id
                            let hasSameStartedAt = moment(r.startedAt).format('HH:mm') == moment(or.startedAt).format('HH:mm')
                            let hasSameEndedAt = moment(r.endedAt).format('HH:mm') == moment(or.endedAt).format('HH:mm')
                            let isCustomized = r.isCustomized
                            if (or.isCustomized == true) {
                                if (isCustomized != true) {
                                    return false
                                }
                                return hasSameDate && hasSameStartedAt && hasSameEndedAt
                            }
                            return hasSameID && hasSameDate && hasSameWorkCodeID
                        }
                        let record = array.find(filter)
                        if (record == undefined) {
                            array.push(deepCopy(or))
                        } else {
                            let index = array.indexOf(record)
                            array[index].employees = or.employees
                        }
                    }
                }
            }

            for (let key in this.worker.oldWorkCodeRecords) {
                let oldRecords = this.worker.oldWorkCodeRecords[key]
                let modifieds = oldRecords.modifiedRecords ?? []
                let customs = modifieds.filter((r) => r.isCustomized == true && r._id == undefined)
                if (customs != undefined && customs?.length > 0) {
                    array = array.concat(customs)
                }
            }

            this.refinedRecords = array
            this.worker.oldWorkCodeRecords = undefined
        },
        checkNeedReload(notification) {
            let startedAt = moment(notification.data.startDate)
            let endedAt = moment(notification.data.endDate)

            let lastPushNotification = this.worker.lastBatchPushNotification
            if (lastPushNotification != undefined) {
                let hospitalID = notification.data.hospitalID
                if (hospitalID == lastPushNotification.hospitalID && startedAt.format() == lastPushNotification.startDate && endedAt.format() == lastPushNotification.endDate) {
                    this.worker.lastBatchPushNotification = undefined
                    return false
                }
            }

            let selectedS = moment(this.selectedDate).startOf('month').startOf('weeks')
            let selectedE = moment(this.selectedDate).endOf('month').endOf('weeks')

            if (startedAt.isSameOrAfter(selectedS) && endedAt.isSameOrBefore(selectedE)) {
                return true
            }
            return false
        },

        // MARK: - Network
        async loadData() {
            this.isLoading = true
            await this.loadWorkRecords()
            await this.loadHoliday()
            this.isLoading = false
        },
        async loadWorkRecords() {
            if (this.startDateString == undefined || this.endDateString == undefined) {
                return
            }
            let hospitalID = this.hospital.selectedHospital._id
            let startedAt = this.startDateString
            let endedAt = this.endDateString

            let model = {
                hospitalID: hospitalID,
                startDate: startedAt,
                endDate: endedAt,
                isRemoved: { $ne: true }
            }

            let array = []
            try {
                // 캐시 공유 배열을 직접 변형(splice)하지 않도록 복사본으로 작업
                array = deepCopy((await this.queryClient.fetchQuery(v2WorkRecordFindDateQueryOptions(model))))
            } catch (err) {
                // 기존 동작 미러링: 봉투 code != 0 이면 로그만 남긴다
                if (!isApiErrorWithCode(err)) throw err
                console.log(err)
                return
            }
            let hasEmployeeRecords = array.filter(r => r.employees.length > 0)
            for (let i=0 ; i<hasEmployeeRecords.length ; i++) {
                let r = hasEmployeeRecords[i]
                let removedEmployees = r.employees.filter(e => e.isRemoved == true)
                for (let ei=0 ; ei<removedEmployees.length ; ei++) {
                    let e = removedEmployees[ei]
                    let employeeIndex = r.employees.indexOf(e)
                    if (employeeIndex < 0) { continue }
                    let recordIndex = array.indexOf(r)
                    if (recordIndex < 0) { continue }
                    array[recordIndex].employees.splice(employeeIndex, 1)
                    if (array[recordIndex].employees.length == 0) {
                        array.splice(recordIndex, 1)
                    }
                }
            }
            this.workRecords = deepCopy(array)
            this.worker.originRecords = deepCopy(array)
            this.refineRecords()
        },
        async loadHoliday() {
            let hospitalID = this.hospital.selectedHospital._id
            let startedAt = this.startDateString
            let endedAt = this.endDateString
            let isRemoved = false
            let model = {
                hospitalID: hospitalID,
                startDate: startedAt,
                endDate: endedAt,
                isRemoved: isRemoved
            }
            try {
                this.holidays = await this.queryClient.fetchQuery(v2WorkHolidayFindDateQueryOptions(model))
            } catch (err) {
                // 기존 동작 미러링: 봉투 code != 0 이면 로그 후 빈 목록
                if (!isApiErrorWithCode(err)) throw err
                console.log(err)
                this.holidays = []
            }
        },
        async updateBatch() {
            this.isLoading = true

            let records = []
            let removalRecords = []

            let allModifiedRecords = this.worker.modifiedRecords
            let origins = []
            let keys = Object.keys(allModifiedRecords)
            for (let i = 0; i < keys.length; i++) {
                let key = keys[i]
                let obj = allModifiedRecords[key]
                origins = origins.concat(obj.modifiedRecords)
                removalRecords = removalRecords.concat(obj.removalRecords)
                if (obj.isModified == false) {
                    continue
                }
                records = records.concat(obj.modifiedRecords)
            }

            let nonCustoms = records.filter((r) => r.isCustomized == false || r.isCustomized == undefined)
            let customs = records.filter((r) => r.isCustomized == true)
            let dateDic = nonCustoms.reduce((acc, item) => {
                let key = moment(item.date).format()
                acc[key] = acc[key] || []
                let model = {
                    hospitalID: item.hospitalID,
                    workCodeID: item.workCodeID._id,
                    date: item.date,
                    employees: item.employees.map((e) => e._id)
                }
                acc[key].push(model)
                return acc
            }, {})

            let emptyCustoms = origins.filter((r) => (r.employees?.length ?? 0) == 0 && r.isCustomized == true)
            removalRecords = removalRecords.concat(emptyCustoms)
            for (let i = 0; i < emptyCustoms.length; i++) {
                let c = emptyCustoms[i]
                let index = customs.indexOf(c)
                customs.splice(index, 1)
            }
            if ((nonCustoms?.length ?? 0) == 0 && (customs?.length ?? 0) == 0 && (removalRecords?.length ?? 0) == 0) {
                this.isLoading = false
                return
            }

            // remove need to remove old one
            for (let i = 0; i < removalRecords.length; i++) {
                let record = removalRecords[i]
                if (record._id == undefined) {
                    continue
                }
                let model = {
                    _id: record._id
                }
                try {
                    await this.removeWorkRecord(model)
                } catch (err) {
                    // 기존 동작 미러링: 봉투 code != 0 이면 로그만 남기고 다음 항목 진행
                    if (!isApiErrorWithCode(err)) throw err
                    console.log(err)
                }
            }

            let hospitalID = this.hospital.selectedHospital._id
            let dates = Object.keys(dateDic)
            for (let i = 0; i < dates.length; i++) {
                let date = dates[i]
                let values = dateDic[date]
                let model = {
                    hospitalID: hospitalID,
                    date: date,
                    datas: values
                }
                try {
                    await this.updateBatchWorkRecord(model)
                } catch (err) {
                    // 기존 동작 미러링: 봉투 code != 0 이면 로그만 남기고 다음 항목 진행
                    if (!isApiErrorWithCode(err)) throw err
                    console.log(err)
                }
            }

            for (let i = 0; i < customs.length; i++) {
                let model = deepCopy(customs[i])
                model.employees = model.employees.map((e) => e._id)
                delete model.modifierID
                delete model.createdAt
                delete model.updatedAt
                try {
                    await this.updateWorkRecord(model)
                } catch (err) {
                    // 기존 동작 미러링: 봉투 code != 0 이면 로그만 남기고 다음 항목 진행
                    if (!isApiErrorWithCode(err)) throw err
                    console.log(err)
                }
            }

            let s = moment(this.selectedDate).startOf('months').startOf('weeks')
            let e = moment(this.selectedDate).endOf('months').endOf('weeks')
            // 기존과 동일하게 fire-and-forget — 실패는 로그만
            this.sendPushWorkRecord({
                data: {
                    hospitalID: hospitalID,
                    startDate: s.format(),
                    endDate: e.format()
                }
            }).catch((err) => console.log(err))
            this.worker.lastBatchPushNotification = {
                hospitalID: hospitalID,
                startDate: s.format(),
                endDate: e.format()
            }

            await this.loadWorkRecords()

            this.isLoading = false
        },

        // MARK: - Key Event Controller
        registKEC() {
            let scrollRouter = this.keyEventController.getNextRouteObject('keydown')
            scrollRouter.groupKey = 'keydown_scroll'
            scrollRouter.target = this._uid
            scrollRouter.policy = this.keyEventController.Enum.RoutingPolicy.Go
            scrollRouter.handler = this.handleScrollEvnet
            this.keyEventController.registRouter(scrollRouter)

            let routerObj = this.keyEventController.getNextRouteObject('keydown')
            routerObj.groupKey = 'keydown_keyinput'
            routerObj.target = this._uid
            routerObj.policy = this.keyEventController.Enum.RoutingPolicy.Stop
            routerObj.handler = this.handleKeyEvent
            this.keyEventController.registRouter(routerObj)
        },
        unregistKEC() {
            this.keyEventController.unregistRouter(this._uid)
        },
        handleScrollEvnet() {
            /* stay empty. - block scroll event with keyboard event */
        },
        handleKeyEvent(e) {
            let comp = this
            let isNotWorkingDay = function (dateString) {
                let dayNumber = moment(dateString).day()
                let time = comp.possibleTimes.find((p) => p.index == dayNumber)
                return !time.isSelected
            }
            let isHoliday = function (dateString) {
                if (comp.holidays.find((h) => moment(h.date).format() == dateString)) {
                    return true
                }
                return false
            }
            let getDay = function (dateString, way) {
                dateString = moment(dateString).startOf('days').add(way, 'days').format()
                if (isHoliday(dateString) == true || isNotWorkingDay(dateString) == true) {
                    return getDay(dateString, way)
                }
                return dateString
            }
            let isDateInRange = function (dateString) {
                if (moment(dateString).isBefore(moment(comp.startDateString)) == true) {
                    return false
                }
                if (moment(dateString).isAfter(moment(comp.endDateString)) == true) {
                    return false
                }
                return true
            }
            let getIndexInRecords = function (records, record) {
                for (let i = 0; i < records.length; i++) {
                    let r = records[i]
                    if (record.isCustomized == true) {
                        if (r.startedAt == record.startedAt && r.endedAt == record.endedAt) {
                            return i
                        }
                    } else {
                        if (r.workCodeID == record.workCodeID || r.workCodeID?._id == record.workCodeID?._id) {
                            return i
                        }
                    }
                }
                return -1
            }
            let getFirstRecord = function () {
                let currentDate = comp.startDateString
                if (isNotWorkingDay(currentDate) == true || isHoliday(currentDate) == true) {
                    currentDate = getDay(currentDate, 1)
                }
                if (isDateInRange(currentDate) == false) {
                    return undefined
                }
                let currentRecords = comp.worker.modifiedRecords[currentDate]?.modifiedRecords ?? []
                if (currentRecords.length == 0) {
                    return undefined
                }
                return currentRecords[0]
            }
            let key = e.key
            // console.log(key)
            let currentFocusedRecord = this.worker.focusedRecord
            if (currentFocusedRecord == undefined) {
                this.worker.focusedRecord = getFirstRecord()
                return
            }
            let currentDate = moment(currentFocusedRecord.date).format()
            let targetDate = currentDate

            let currentRecords = this.worker.modifiedRecords[targetDate]?.modifiedRecords ?? []
            let currentIndex = getIndexInRecords(currentRecords, currentFocusedRecord)
            let targetIndex = currentIndex
            let needOpenSearch = false

            if (key == 'Tab') {
                key = 'ArrowRight'
                if (e.shiftKey == true) {
                    key = 'ArrowLeft'
                }
            }

            switch (key) {
                case 'ArrowDown':
                    {
                        e.stopPropagation()
                        targetIndex = currentIndex + 1
                        if (targetIndex >= currentRecords.length) {
                            targetIndex = 0
                            targetDate = getDay(currentDate, 7)
                        }
                    }
                    break
                case 'ArrowUp':
                    {
                        e.stopPropagation()
                        targetIndex = currentIndex - 1
                        if (targetIndex < 0) {
                            targetDate = getDay(currentDate, -7)
                            let targetRecords = this.worker.modifiedRecords[targetDate]?.modifiedRecords ?? []
                            targetIndex = targetRecords.length - 1
                        }
                    }
                    break
                case 'ArrowLeft':
                    {
                        e.stopPropagation()
                        targetDate = getDay(currentDate, -1)
                        let targetRecords = this.worker.modifiedRecords[targetDate]?.modifiedRecords ?? []
                        if (targetIndex >= targetRecords.length) {
                            targetIndex = targetRecords.length - 1
                        }
                    }
                    break
                case 'ArrowRight':
                    {
                        e.stopPropagation()
                        targetDate = getDay(currentDate, 1)
                        let targetRecords = this.worker.modifiedRecords[targetDate]?.modifiedRecords ?? []
                        if (targetIndex >= targetRecords.length) {
                            targetIndex = targetRecords.length - 1
                        }
                    }
                    break
                case 'Enter':
                    {
                        e.stopPropagation()
                        needOpenSearch = true
                    }
                    break
                case 'Delete': {
                    if (this.worker.focusedRecord == undefined) {
                        return
                    }
                    this.worker.deleteEmployeesInRecord = true
                    return
                }
                default:
                    return
            }
            if (isDateInRange(targetDate) == false) {
                targetDate = currentDate
                targetIndex = currentIndex
            }
            let targetRecords = this.worker.modifiedRecords[targetDate]?.modifiedRecords ?? []
            // console.log(targetDate, targetIndex)
            if (targetRecords.length == 0) {
                return
            }
            this.worker.focusedRecord = targetRecords[targetIndex]
            if (needOpenSearch) {
                showSearchLayer({
                    _id: this.worker.focusedRecord._id,
                    hospitalID: this.worker.focusedRecord.hospitalID,
                    date: this.worker.focusedRecord.date,
                    workCodeID: this.worker.focusedRecord.workCodeID,
                    isCustomized: this.worker.focusedRecord.isCustomized,
                    startedAt: this.worker.focusedRecord.startedAt,
                    endedAt: this.worker.focusedRecord.endedAt
                })
            }
        },
        moveToElement(item) {
            if (!this.isEditMode) return

            let getOffsetTop = function (el) {
                let element = el
                let offset = 0
                while (element != undefined) {
                    offset += element.offsetTop
                    element = element.offsetParent
                }
                return offset
            }

            let scroll = this.$refs.scroll.offsetParent
            let scrollTop = scroll.scrollTop
            let scrollFrameHeight = scroll.offsetHeight
            let scrollOffsetTop = getOffsetTop(scroll)
            let itemOffsetTop = getOffsetTop(item)
            let itemHeight = item.offsetHeight

            let topOrigin = scrollOffsetTop + scrollTop + scrollFrameHeight + 30 - itemOffsetTop
            let bottomOrigin = scrollOffsetTop + scrollTop + scrollFrameHeight - (itemOffsetTop + itemHeight + 30)
            if (bottomOrigin < 0) {
                scroll.scrollTop = scrollTop - bottomOrigin
            }
            if (topOrigin > scrollFrameHeight) {
                scroll.scrollTop = scrollTop + (scrollFrameHeight - topOrigin)
            }
        }
    }
}
</script>
